import React, { useState, useMemo } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

function computePoints(peakMult: number, rampDays: number, curve: 'linear' | 'ease_in') {
  const points: { day: string; multiplier: number }[] = []

  for (let i = 1; i <= rampDays; i++) {
    const progress = i / (rampDays + 1)
    const adjusted = curve === 'ease_in' ? progress * progress : progress
    const mult = 1 + (peakMult - 1) * adjusted
    points.push({ day: `Day -${rampDays - i + 1}`, multiplier: Math.round(mult * 1000) / 1000 })
  }

  points.push({ day: 'Peak', multiplier: peakMult })

  for (let i = rampDays; i >= 1; i--) {
    const progress = i / (rampDays + 1)
    const adjusted = curve === 'ease_in' ? progress * progress : progress
    const mult = 1 + (peakMult - 1) * adjusted
    points.push({ day: `Day +${rampDays - i + 1}`, multiplier: Math.round(mult * 1000) / 1000 })
  }

  return points
}

export default function RampCurveCompare() {
  const [peakMult, setPeakMult] = useState(1.5)
  const [rampDays, setRampDays] = useState(3)

  const linearData = useMemo(() => computePoints(peakMult, rampDays, 'linear'), [peakMult, rampDays])
  const easeInData = useMemo(() => computePoints(peakMult, rampDays, 'ease_in'), [peakMult, rampDays])

  const mergedData = useMemo(() => {
    return linearData.map((lp, i) => ({
      day: lp.day,
      linear: lp.multiplier,
      easeIn: easeInData[i].multiplier,
    }))
  }, [linearData, easeInData])

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Label htmlFor="ramp-peak" className="text-sm font-medium whitespace-nowrap">Peak multiplier</Label>
          <Input
            id="ramp-peak"
            type="number"
            min={1.1}
            max={3}
            step={0.1}
            value={peakMult}
            onChange={(e) => setPeakMult(Math.max(1.1, Math.min(3, Number(e.target.value) || 1.5)))}
            className="h-9 w-20 text-sm tabular-nums font-semibold"
          />
          <span className="text-xs text-muted-foreground">x</span>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="ramp-days" className="text-sm font-medium whitespace-nowrap">Ramp days</Label>
          <Input
            id="ramp-days"
            type="number"
            min={1}
            max={7}
            step={1}
            value={rampDays}
            onChange={(e) => setRampDays(Math.max(1, Math.min(7, Math.round(Number(e.target.value)) || 3)))}
            className="h-9 w-16 text-sm tabular-nums font-semibold"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-border/60 p-4">
          <p className="text-xs font-semibold text-foreground mb-2">Linear</p>
          <p className="text-[11px] text-muted-foreground mb-3">Even steps -- same boost each day</p>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={mergedData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis domain={[1, 'auto']} tick={{ fontSize: 9, fill: '#64748b' }} tickFormatter={(v: number) => `${v}x`} tickLine={false} axisLine={false} />
              <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="3 3" />
              <Tooltip formatter={(v: number | undefined) => [`${v ?? 0}x`, 'Linear']} contentStyle={{ fontSize: 10, borderRadius: 6 }} />
              <Area type="monotone" dataKey="linear" stroke="#0d9488" strokeWidth={2} fill="#0d948820" dot={{ r: 2, fill: '#0d9488' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border/60 p-4">
          <p className="text-xs font-semibold text-foreground mb-2">Ease-in</p>
          <p className="text-[11px] text-muted-foreground mb-3">Slow start, fast finish -- accelerates toward peak</p>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={mergedData} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis domain={[1, 'auto']} tick={{ fontSize: 9, fill: '#64748b' }} tickFormatter={(v: number) => `${v}x`} tickLine={false} axisLine={false} />
              <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="3 3" />
              <Tooltip formatter={(v: number | undefined) => [`${v ?? 0}x`, 'Ease-in']} contentStyle={{ fontSize: 10, borderRadius: 6 }} />
              <Area type="monotone" dataKey="easeIn" stroke="#f59e0b" strokeWidth={2} fill="#f59e0b20" dot={{ r: 2, fill: '#f59e0b' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        With {rampDays} ramp days and a {peakMult}x peak, <strong className="text-foreground">linear</strong> adds{' '}
        <span className="tabular-nums font-semibold text-teal-700">+{((mergedData[0]?.linear ?? 1) - 1).toFixed(3)}x</span> on the first ramp day, while{' '}
        <strong className="text-foreground">ease-in</strong> adds only{' '}
        <span className="tabular-nums font-semibold text-amber-600">+{((mergedData[0]?.easeIn ?? 1) - 1).toFixed(3)}x</span> -- saving the big increase for closer to the event.
      </p>
    </div>
  )
}
